import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import HistoryIcon from '@mui/icons-material/History';
import { PortalMcpLogOut as McpLog } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';

interface Props {
  logs: McpLog[];
  loading: boolean;
}

export default function ServerLogsTab({ logs, loading }: Props) {
  const t = useTranslation();
  return (
    <Card
      sx={{
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <HistoryIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('logsTab.title')}</Typography>
      </Box>
      {loading ? (
        <Box sx={{ p: 3 }}>
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : logs.length === 0 ? (
        <Box sx={{ px: 3, pb: 3 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            {t('logsTab.empty')}
          </Typography>
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('common.col.date')}</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('logsTab.col.tool')}</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('logsTab.col.user')}</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('common.col.status')}</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('logsTab.col.responseTime')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Typography sx={{ fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums' }}>
                    {new Date(log.created).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: '0.8rem', fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>
                    {log.tool_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {log.username}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={log.is_success ? t('logsTab.status.success') : t('logsTab.status.error')}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                      bgcolor: log.is_success ? '#e8f5ef' : '#fef2f2',
                      color: log.is_success ? '#007347' : '#C24242',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {log.response_time_ms} ms
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
